import { useRequiredContext } from "@redotech/react-util/context";
import type { CustomAnalyticsReportSort } from "@redotech/redo-analytics-common/reports/custom-analytics-report";
import type { CustomAnalyticsReportFilter } from "@redotech/redo-analytics-common/reports/custom-analytics-report-filters";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { useInfiniteQuery } from "@tanstack/react-query";

const CUSTOM_ANALYTICS_REPORT_ROWS_PAGE_SIZE = 25;
const CUSTOM_ANALYTICS_REPORT_ROWS_QUERY_KEY = "customAnalyticsReportRows";

export function getCustomAnalyticsReportRowsQueryKey(
  teamId: string,
  customReportId: string,
  reportTimeZone?: string,
  sort?: CustomAnalyticsReportSort | null,
  filters?: readonly CustomAnalyticsReportFilter[],
) {
  return [
    CUSTOM_ANALYTICS_REPORT_ROWS_QUERY_KEY,
    teamId,
    customReportId,
    reportTimeZone ?? null,
    sort?.key ?? null,
    sort?.direction ?? null,
    filters ?? [],
  ] as const;
}

export function getCustomAnalyticsReportRowsQueryInvalidationKey(
  teamId: string,
  customReportId?: string,
) {
  return customReportId
    ? ([
        CUSTOM_ANALYTICS_REPORT_ROWS_QUERY_KEY,
        teamId,
        customReportId,
      ] as const)
    : ([CUSTOM_ANALYTICS_REPORT_ROWS_QUERY_KEY, teamId] as const);
}

export function useCustomAnalyticsReportRowsQuery({
  customReportId,
  reportTimeZone,
  sort,
  filters = [],
  enabled = true,
}: {
  customReportId: string;
  reportTimeZone?: string;
  sort?: CustomAnalyticsReportSort | null;
  filters?: readonly CustomAnalyticsReportFilter[];
  enabled?: boolean;
}) {
  const team = useRequiredContext(TeamContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  return useInfiniteQuery({
    queryKey: getCustomAnalyticsReportRowsQueryKey(
      team._id,
      customReportId,
      reportTimeZone,
      sort,
      filters,
    ),
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      return await rpcClient.getCustomAnalyticsReportRows({
        id: customReportId,
        sort,
        filters: [...filters],
        reportTimeZone,
        offset: pageParam,
        limit: CUSTOM_ANALYTICS_REPORT_ROWS_PAGE_SIZE,
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
    staleTime: 10 * 1000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    enabled,
  });
}
